/**
 * useUserMedia Hook
 *
 * Feature-specific hook for fetching and managing user medias with pagination.
 * Part of clean architecture: Component → Hook → UseCase → Service
 *
 * Pattern:
 * - Encapsulates business logic for user media fetching
 * - Manages local state for medias, pagination, loading
 * - Does NOT use React Query (component-level state only)
 * - Returns actions and state for the UI
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useStore } from 'react-redux';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { createFetchUserMediasUseCase } from '@/domain/usecases/userMedia/fetchUserMediasUseCase';
import { selectHasActiveSubscription } from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';
import { subscribeToUserMediaLiveChangesUseCase } from '@/domain/usecases/userMedia/subscribeToUserMediaLiveChangesUseCase';
import { clearPendingProcessingMediaUseCase } from '@/domain/usecases/userMedia/clearPendingProcessingMediaUseCase';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { RootState } from '@/presentation/store';
import type { PendingProcessingMediaStorage } from '@/domain/entities/secureStorage/pendingProcessingMediaStorage';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { Unsubscribe } from 'firebase/firestore';
import type { PageCursor } from '@/data/types/json';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseUserMediasResult {
    medias: UserMediaEntity[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    error: Error | null;
    fetchMedias: () => Promise<void>;
    loadMore: () => Promise<void>;
    refresh: () => Promise<void>;
}

/**
 * Hook for fetching user medias with pagination.
 *
 * @param userId - The Firebase ID of the user to fetch medias for
 * @param isCurrentUser - When true, attaches Firestore live-change listeners for
 *   any pending-processing media in secure storage so the owner's grid updates
 *   without a manual refresh. Pass `false` for other users' profiles.
 * @param limit - Number of items to fetch per page (default: 20)
 * @returns User medias state and actions
 *
 * @example
 * ```tsx
 * const isCurrentUser = currentUser?.firebaseId === userId;
 * const { medias, isLoading, hasMore, fetchMedias, loadMore } = useUserMedias(userId, isCurrentUser);
 *
 * useEffect(() => {
 *   fetchMedias();
 * }, []);
 *
 * return (
 *   <div>
 *     {medias.map(media => <MediaCard key={media.id} media={media} />)}
 *     {hasMore && <button onClick={loadMore}>Load More</button>}
 *   </div>
 * );
 * ```
 */
export const useUserMedias = (
    userId: string | undefined,
    isCurrentUser: boolean,
    limit: number = 20,
): UseUserMediasResult => {
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const store = useStore<RootState>();
    const hasActiveSubscription = useCallback(
        (creatorUserId: string) => selectHasActiveSubscription(store.getState(), creatorUserId),
        [store],
    );
    const [medias, setMedias] = useState<UserMediaEntity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef(false);

    // Keep ref in sync with state so external consumers can read the latest value
    useEffect(() => {
        isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    const [hasMore, setHasMore] = useState(false); // false until fetchMedias sets it from the API response
    const [error, setError] = useState<Error | null>(null);
    const [lastFetchedItem, setLastFetchedItem] = useState<PageCursor | undefined>();

    /**
     * Fetch initial user medias
     */
    const fetchMedias = useCallback(async () => {
        if (!userId) {
            setError(new Error('User ID is required'));
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const useCase = createFetchUserMediasUseCase();
            const result = await useCase.execute(userId, currentUser, hasActiveSubscription, undefined, limit);

            // Preserve any optimistic local entities not yet present on the server,
            // otherwise a fast IndexedDB read of pending items can be clobbered by
            // the slower Firestore fetch landing afterwards.
            setMedias(prev => {
                const serverIds = new Set(result.medias.map(m => m.id));
                const pendingLocal = prev.filter(m => m.isLocal && !serverIds.has(m.id));
                return [...pendingLocal, ...result.medias];
            });
            setHasMore(result.hasMore);
            setLastFetchedItem(result.cursor);
        } catch (err) {
            appLogger.error('Error fetching medias', 'useUserMedia.fetchMedias', { error: err as Error });
            setError(err instanceof Error ? err : new Error('Failed to fetch medias'));
            setMedias(prev => prev.filter(m => m.isLocal));
        } finally {
            setIsLoading(false);
        }
    }, [userId, currentUser, hasActiveSubscription, limit]);

    /**
     * Load more medias (pagination)
     */
    const loadMore = useCallback(async () => {
        if (!userId || !hasMore || isLoadingMoreRef.current) {
            return;
        }

        setIsLoadingMore(true);
        isLoadingMoreRef.current = true; // Set immediately to block re-entrant calls before re-render

        setError(null);

        try {
            const useCase = createFetchUserMediasUseCase();
            const result = await useCase.execute(userId, currentUser, hasActiveSubscription, lastFetchedItem, limit);

            setMedias(prev => [...prev, ...result.medias]);
            setHasMore(result.hasMore);
            setLastFetchedItem(result.cursor);
        } catch (err) {
            appLogger.error('Error loading more medias', 'useUserMedia.loadMore', { error: err as Error });
            setError(err instanceof Error ? err : new Error('Failed to load more medias'));
        } finally {
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    }, [userId, currentUser, hasActiveSubscription, hasMore, lastFetchedItem, limit]);

    // Keep the fetched medias in sync with global events.
    // These listeners are required so that `fetchedMedias` (returned from this hook) stays
    // up to date — the page mirrors it via a useEffect. Identical listeners also exist on
    // the page itself to cover "initialMedias" mode, where `fetchedMedias` is never
    // populated and these hook-level listeners would have no effect on the displayed list.
    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, ({ mediaId }) => {
            setMedias(prev => prev.filter(m => m.id !== mediaId));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, ({ mediaId, likes }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, likes } : m));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, ({ mediaId, comments }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, comments } : m));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, ({ mediaId, patch }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, ...patch } : m));
        });
    }, []);

    // Pending processing media (owner's just-published items) — only on own profile.
    // Mirrors Flutter's user_details_screen.dart:_listenToMediaLiveChanges flow
    // (lib/presentation/screens/user/user_details_screen.dart:280-352).
    const { items: pendingItems } = useListenToSecureStorageUpdatesByPrefix<PendingProcessingMediaStorage>(
        SECURE_STORAGE_KEYS.PENDING_PROCESSING_MEDIA,
        (pending) => pending.name,
        true,
    );
    const liveSubscriptionsRef = useRef<Map<string, Unsubscribe>>(new Map());

    useEffect(() => {
        if (!isCurrentUser || !userId) return;

        // Iterate ascending by createdTimestamp so the newest pending item
        // ends up at the head after each unshift (`[media, ...prev]`).
        // IndexedDB's getAll(range) returns items ordered by key (mediaId
        // suffix), which has nothing to do with save time.
        const orderedPending = [...pendingItems].sort(
            (a, b) => (a.media.createdTimestamp ?? 0) - (b.media.createdTimestamp ?? 0),
        );

        for (const pending of orderedPending) {
            const { media } = pending;
            // Safety net in case a different account is signed in while entries remain
            if (media.firebaseId !== userId) continue;

            // Insert into the grid if not already present (idempotent)
            setMedias(prev => {
                const alreadyPresent = prev.some(m => m.id === media.id);
                return alreadyPresent ? prev : [media, ...prev];
            });

            // Attach a live Firestore listener (idempotent)
            if (liveSubscriptionsRef.current.has(media.id)) continue;

            const unsub = subscribeToUserMediaLiveChangesUseCase.execute(
                userId,
                media.id,
                ({ type, media: updated }) => {
                    // Skip intermediate updates while still processing. The cloud
                    // function writes the thumbnail path before the file is on the
                    // CDN; applying those updates would render a thumbnail that
                    // 404s and latches CachedImage into an error state. Keep the
                    // local entity in place until isProcessing flips to false.
                    if (type !== 'removed' && updated.isProcessing) return;

                    setMedias(prev => {
                        const index = prev.findIndex(m => m.id === updated.id);
                        if (type === 'removed') return prev.filter(m => m.id !== updated.id);
                        if (index === -1) return [updated, ...prev];
                        const next = [...prev];
                        next[index] = updated;
                        return next;
                    });

                    liveSubscriptionsRef.current.get(updated.id)?.();
                    liveSubscriptionsRef.current.delete(updated.id);
                    void clearPendingProcessingMediaUseCase.execute(updated.id).catch((error) => {
                        appLogger.error(
                            'Failed to clear pending processing media',
                            'useUserMedia.subscribeLiveChanges', { error: error as Error },
                        );
                    });
                },
            );
            liveSubscriptionsRef.current.set(media.id, unsub);
        }
    }, [pendingItems, isCurrentUser, userId]);

    // Detach live subscriptions when the active profile changes (userId flip,
    // sign-out, or isCurrentUser=false), and on unmount. The subscription effect
    // above re-attaches for the new (userId, isCurrentUser) on the next pass.
    useEffect(() => {
        const subscriptions = liveSubscriptionsRef.current;
        return () => {
            subscriptions.forEach(unsub => unsub());
            subscriptions.clear();
        };
    }, [isCurrentUser, userId]);

    /**
     * Refresh medias (clear and fetch again)
     */
    const refresh = useCallback(async () => {
        // Don't wipe medias here — fetchMedias's merge preserves local pending
        // tiles and replaces server items, avoiding a flicker on refresh.
        setLastFetchedItem(undefined);
        setHasMore(false); // Reset to false — fetchMedias will set the correct value
        await fetchMedias();
    }, [fetchMedias]);

    return {
        medias,
        isLoading,
        isLoadingMore,
        hasMore,
        error,
        fetchMedias,
        loadMore,
        refresh,
    };
};
